import {
  readStringValue,
} from './assembly-node-utils'
import type {
  AssemblyModelAsset,
  AssemblyNode,
  PartAsset,
} from './types'

/**
 * 判断引用 key 是否为 loader 内部生成的内联资产 key。
 *
 * 设计说明：
 * 1. `inline:` 与 `inline-assembly:` 只表示当前 GLB 内部几何，不需要外部下载；
 * 2. 外部引用 fallback 只能作用于真实 external_file_uuid 节点；
 * 3. 这里集中判断，避免不同组合路径对内联 key 的过滤规则发散。
 *
 * @param value 候选引用 key。
 * @returns 是否为内联引用 key。
 */
function isInlineReferenceKey(value: string | undefined): boolean {
  return value?.startsWith('inline:') === true || value?.startsWith('inline-assembly:') === true
}

/**
 * 解析节点的源实体 ID。
 *
 * 设计说明：
 * 1. 导出器会用 source_entity_id 标识同一个 CAD 实体的 occurrence；
 * 2. fallback carrier 归属需要依赖该稳定 ID，而不是依赖展示名称；
 * 3. 同时兼容 snake_case 与 camelCase 字段，避免不同导出版本解析失败。
 *
 * @param records 节点语义记录集合。
 * @returns 源实体 ID。
 */
export function resolveNodeSourceEntityId(records: Record<string, unknown>[]): string | undefined {
  return readStringValue(records, ['source_entity_id', 'sourceEntityId'])
}

/**
 * 解析节点的源实体路径。
 *
 * @param records 节点语义记录集合。
 * @returns 源实体路径。
 */
export function resolveNodeSourceEntityPath(records: Record<string, unknown>[]): string | undefined {
  return readStringValue(records, ['source_entity_path', 'sourceEntityPath'])
}

/**
 * 判断源实体路径是否位于指定实体之下。
 *
 * 设计说明：source_entity_path 使用冒号表达实体层级，因此只接受完全相等或 `id:` 前缀，
 * 避免 `abc` 误匹配 `abcd` 这类普通字符串前缀。
 *
 * @param sourceEntityPath 候选源实体路径。
 * @param sourceEntityId 外部引用 occurrence 的源实体 ID。
 * @returns 是否属于该外部引用 occurrence。
 */
function isSourcePathUnderEntity(sourceEntityPath: string | undefined, sourceEntityId: string): boolean {
  return sourceEntityPath === sourceEntityId || sourceEntityPath?.startsWith(`${sourceEntityId}:`) === true
}

/**
 * 将导出器拆平的外部引用 fallback carrier 重新挂到对应 occurrence 下。
 *
 * 设计说明：
 * 1. 部分 GLB 会把 external_file_uuid occurrence 与同源 mesh carrier 导出成兄弟节点；
 * 2. 当外部引用下载失败时，carrier 仍可作为本地 fallback 展示；
 * 3. 当外部引用下载成功时，组合阶段会隐藏该 carrier，避免外部资产与本地 fallback 重复显示。
 *
 * @param nodesById 装配 manifest 节点索引。
 * @param childIdsByNodeId 父子关系索引。
 * @param sourceEntityIdByNodeId 节点到源实体 ID 的索引。
 * @param sourceEntityPathByNodeId 节点到源实体路径的索引。
 */
export function reparentFlattenedExternalReferenceCarriers(
  nodesById: Record<string, AssemblyNode>,
  childIdsByNodeId: Record<string, string[]>,
  sourceEntityIdByNodeId: Record<string, string>,
  sourceEntityPathByNodeId: Record<string, string>,
): void {
  const externalReferenceNodes = Object.entries(nodesById)
    .map(([nodeId, node]) => ({
      nodeId,
      node,
      sourceEntityId: sourceEntityIdByNodeId[nodeId],
    }))
    .filter((item): item is { nodeId: string; node: AssemblyNode; sourceEntityId: string } => (
      Boolean(item.node.partFileUuid)
      && !isInlineReferenceKey(item.node.partFileUuid)
      && Boolean(item.sourceEntityId)
    ))
    .sort((left, right) => right.sourceEntityId.length - left.sourceEntityId.length)

  if (externalReferenceNodes.length === 0) {
    return
  }

  Object.entries(nodesById).forEach(([nodeId, node]) => {
    if (node.partFileUuid && !isInlineReferenceKey(node.partFileUuid)) {
      return
    }

    const sourceEntityPath = sourceEntityPathByNodeId[nodeId]
    if (!sourceEntityPath) {
      return
    }

    const owner = externalReferenceNodes.find((item) => (
      item.nodeId !== nodeId && isSourcePathUnderEntity(sourceEntityPath, item.sourceEntityId)
    ))
    if (!owner || node.parentNodeId === owner.nodeId) {
      if (owner) {
        node.externalReferenceFallbackForNodeId = owner.nodeId
      }
      return
    }

    if (node.parentNodeId) {
      childIdsByNodeId[node.parentNodeId] = (childIdsByNodeId[node.parentNodeId] ?? [])
        .filter((childId) => childId !== nodeId)
    }
    node.parentNodeId = owner.nodeId
    node.externalReferenceFallbackForNodeId = owner.nodeId
    const ownerChildren = childIdsByNodeId[owner.nodeId] ?? []
    if (!ownerChildren.includes(nodeId)) {
      childIdsByNodeId[owner.nodeId] = [...ownerChildren, nodeId]
    }
  })
}

/**
 * 判断已成功解析外部资产时是否应隐藏 fallback carrier。
 *
 * 设计说明：
 * 1. fallback carrier 只在外部引用缺失时展示，用于保留导入模型的局部可见性；
 * 2. 一旦真实外部 PartAsset/AssemblyAsset 已加载，继续展示 carrier 会造成重复几何；
 * 3. 因此只在父节点已有真实外部资产且子节点标记归属该父节点时跳过。
 *
 * @param parentNode 当前装配节点。
 * @param childNode 候选子节点。
 * @param partAsset 父节点已解析出的外部零件资产。
 * @param assemblyAsset 父节点已解析出的外部装配资产。
 * @returns 是否应在组合场景和设计树中跳过该子节点。
 */
export function shouldSkipResolvedExternalReferenceFallback(
  parentNode: AssemblyNode,
  childNode: AssemblyNode | undefined,
  partAsset: PartAsset | undefined,
  assemblyAsset: AssemblyModelAsset | undefined,
): boolean {
  return Boolean(
    parentNode.partFileUuid
    && !isInlineReferenceKey(parentNode.partFileUuid)
    && (partAsset || assemblyAsset)
    && childNode?.externalReferenceFallbackForNodeId === parentNode.nodeId,
  )
}
